import {
  ALGO_BLOCKCHAIN,
  ALGO_BLOCKCHAIN_WITHDRAWAL,
  APTOS_BLOCKCHAIN,
  APTOS_BLOCKCHAIN_WITHDRAWAL,
  ARB_BLOCKCHAIN,
  ARB_BLOCKCHAIN_WITHDRAWAL,
  AVAX_BLOCKCHAIN,
  AVAX_BLOCKCHAIN_EURC,
  AVAX_BLOCKCHAIN_WITHDRAWAL,
  AVAX_BLOCKCHAIN_WITHDRAWAL_EURC,
  BASE_BLOCKCHAIN,
  BASE_BLOCKCHAIN_EURC,
  BASE_BLOCKCHAIN_WITHDRAWAL,
  BASE_BLOCKCHAIN_WITHDRAWAL_EURC,
  BTC_BLOCKCHAIN,
  BTC_BLOCKCHAIN_WITHDRAWAL,
  CELO_BLOCKCHAIN,
  CELO_BLOCKCHAIN_WITHDRAWAL,
  CODEX_BLOCKCHAIN,
  CODEX_BLOCKCHAIN_WITHDRAWAL,
  ETH_BLOCKCHAIN,
  ETH_BLOCKCHAIN_EURC,
  ETH_BLOCKCHAIN_WITHDRAWAL,
  ETH_BLOCKCHAIN_WITHDRAWAL_EURC,
  EVMOS_BLOCKCHAIN,
  EVMOS_BLOCKCHAIN_WITHDRAWAL,
  FLOW_BLOCKCHAIN,
  FLOW_BLOCKCHAIN_WITHDRAWAL,
  HBAR_BLOCKCHAIN,
  HBAR_BLOCKCHAIN_WITHDRAWAL,
  LINEA_BLOCKCHAIN,
  LINEA_BLOCKCHAIN_WITHDRAWAL,
  MANA_BLOCKCHAIN,
  MANA_BLOCKCHAIN_WITHDRAWAL,
  MATIC_BLOCKCHAIN,
  MATIC_BLOCKCHAIN_WITHDRAWAL,
  NEAR_BLOCKCHAIN,
  NEAR_BLOCKCHAIN_WITHDRAWAL,
  NOBLE_BLOCKCHAIN,
  NOBLE_BLOCKCHAIN_WITHDRAWAL,
  OP_BLOCKCHAIN,
  OP_BLOCKCHAIN_WITHDRAWAL,
  PAH_BLOCKCHAIN,
  PAH_BLOCKCHAIN_WITHDRAWAL,
  POLY_BLOCKCHAIN,
  POLY_BLOCKCHAIN_WITHDRAWAL,
  SEI_BLOCKCHAIN,
  SEI_BLOCKCHAIN_WITHDRAWAL,
  SOL_BLOCKCHAIN,
  SOL_BLOCKCHAIN_EURC,
  SOL_BLOCKCHAIN_WITHDRAWAL,
  SOL_BLOCKCHAIN_WITHDRAWAL_EURC,
  SONIC_BLOCKCHAIN,
  SONIC_BLOCKCHAIN_WITHDRAWAL,
  SUI_BLOCKCHAIN,
  SUI_BLOCKCHAIN_WITHDRAWAL,
  TRX_BLOCKCHAIN,
  TRX_BLOCKCHAIN_WITHDRAWAL,
  UNI_BLOCKCHAIN,
  UNI_BLOCKCHAIN_WITHDRAWAL,
  WORLDCHAIN_BLOCKCHAIN,
  WORLDCHAIN_BLOCKCHAIN_WITHDRAWAL,
  XLM_BLOCKCHAIN,
  XLM_BLOCKCHAIN_EURC,
  XLM_BLOCKCHAIN_WITHDRAWAL,
  XLM_BLOCKCHAIN_WITHDRAWAL_EURC,
  XRP_BLOCKCHAIN,
  XRP_BLOCKCHAIN_WITHDRAWAL,
  ZKS_BLOCKCHAIN,
  ZKS_BLOCKCHAIN_WITHDRAWAL,
} from '@services/permissions'
import { type SynthPermissionRole, SynthRole } from '@services/synthetic-roles'
import {
  AssetIcon,
  BlockchainAbbreviation,
  BlockchainActivityType,
  Currency,
  type Maybe,
} from '@shared/graphql'

import type { PermissionSet } from '@circlefin/permissions'

interface DepositData {
  min: number
  max: number
  duration: 'hours'
}

interface AddressData {
  test: string
  live: string
}

interface BlockchainRole {
  default: SynthPermissionRole
  withdrawal: SynthPermissionRole
}

export interface Blockchain<Chain> {
  chain: Chain
  name: string
  explorerLink: string
  testnetExplorerLink: string
  hasMemo: boolean
  assets: Currency[]
  isBridged: boolean
  icon: AssetIcon
  isAceSupported: boolean
  chainId?: Maybe<number>
  testnetChainId?: Maybe<number>
  deposits?: DepositData
  usdcAddress?: AddressData
  tokenMessengerAddress?: AddressData
  messageTransmitterAddress?: AddressData
  usdcRole: BlockchainRole
  eurcRole?: BlockchainRole
}

type Blockchains = {
  [Chain in BlockchainAbbreviation]: Blockchain<Chain>
}

type BlockchainAssetPermissions = {
  [Chain in BlockchainAbbreviation]: {
    [Asset in Currency]?: {
      [Level in BlockchainActivityType]?: PermissionSet
    }
  }
}

/**
 * Static Data: Blockchain Details For All Supported Chains in Circle Mint.
 */
export const BLOCKCHAINS: Blockchains = {
  [BlockchainAbbreviation.ALGO]: {
    chain: BlockchainAbbreviation.ALGO,
    name: 'Algorand',
    explorerLink: 'https://allo.info/tx/{{txHash}}',
    testnetExplorerLink:
      'https://testnet.explorer.perawallet.app/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.ALGO,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_ALGO_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_ALGO_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.APTOS]: {
    chain: BlockchainAbbreviation.APTOS,
    name: 'Aptos',
    explorerLink:
      'https://explorer.aptoslabs.com/txn/{{txHash}}?network=mainnet',
    testnetExplorerLink:
      'https://explorer.aptoslabs.com/txn/{{txHash}}?network=testnet',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.APTOS,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_APTOS_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_APTOS_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.ARB]: {
    chain: BlockchainAbbreviation.ARB,
    name: 'Arbitrum',
    explorerLink: 'https://arbiscan.io/tx/{{txHash}}',
    testnetExplorerLink: 'https://sepolia.arbiscan.io/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.ARB,
    isAceSupported: false,
    chainId: 42161,
    testnetChainId: 421614,
    usdcRole: {
      default: SynthRole.SYNTH_ARB_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_ARB_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.AVAX]: {
    chain: BlockchainAbbreviation.AVAX,
    name: 'Avalanche',
    explorerLink: 'https://snowtrace.io/tx/{{txHash}}',
    testnetExplorerLink: 'https://testnet.snowtrace.io/tx/{{txHash}}',
    assets: [Currency.USD, Currency.EUR],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.AVAX,
    isAceSupported: false,
    chainId: 43114,
    testnetChainId: 43113,
    usdcRole: {
      default: SynthRole.SYNTH_AVAX_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_AVAX_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
    eurcRole: {
      default: SynthRole.SYNTH_AVAX_BLOCKCHAIN_EURC_ROLE,
      withdrawal: SynthRole.SYNTH_AVAX_WITHDRAWAL_BLOCKCHAIN_EURC_ROLE,
    },
  },
  [BlockchainAbbreviation.BTC]: {
    chain: BlockchainAbbreviation.BTC,
    name: 'Bitcoin',
    explorerLink: 'https://blockstream.info/tx/{{txHash}}',
    testnetExplorerLink: 'https://blockstream.info/testnet/tx/{{txHash}}',
    assets: [],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.BTC,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_BTC_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_BTC_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.BASE]: {
    chain: BlockchainAbbreviation.BASE,
    name: 'Base',
    explorerLink: 'https://basescan.org/tx/{{txHash}}',
    testnetExplorerLink: 'https://sepolia.basescan.org/tx/{{txHash}}',
    assets: [Currency.USD, Currency.EUR],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.BASE,
    isAceSupported: false,
    chainId: 8453,
    testnetChainId: 84532,
    usdcRole: {
      default: SynthRole.SYNTH_BASE_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_BASE_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
    eurcRole: {
      default: SynthRole.SYNTH_BASE_BLOCKCHAIN_EURC_ROLE,
      withdrawal: SynthRole.SYNTH_BASE_WITHDRAWAL_BLOCKCHAIN_EURC_ROLE,
    },
  },
  [BlockchainAbbreviation.CELO]: {
    chain: BlockchainAbbreviation.CELO,
    name: 'Celo',
    explorerLink: 'https://celoscan.io/tx/{{txHash}}',
    testnetExplorerLink: 'https://alfajores.celoscan.io/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.CELO,
    isAceSupported: false,
    chainId: 42220,
    testnetChainId: 44787,
    usdcRole: {
      default: SynthRole.SYNTH_CELO_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_CELO_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.CODEX]: {
    chain: BlockchainAbbreviation.CODEX,
    name: 'Codex',
    explorerLink: 'https://explorer.codex.xyz/tx/{{txHash}}',
    testnetExplorerLink: 'https://explorer.codex-stg.xyz/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.CODEX,
    isAceSupported: true,
    chainId: 81224,
    testnetChainId: 812242,
    usdcAddress: {
      test: '0x6d7f141b6819C2c9CC2f818e6ad549E7Ca090F8f',
      live: '0xd996633a415985DBd7D6D12f4A4343E31f5037cf',
    },
    tokenMessengerAddress: {
      test: '0x8FE6B999Dc680CcFDD5Bf7EB0974218be2542DAA',
      live: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    },
    messageTransmitterAddress: {
      test: '0xE737e5cEBEEBa77EFE34D4aa090756590b1CE275',
      live: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    },
    usdcRole: {
      default: SynthRole.SYNTH_CODEX_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_CODEX_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.ETH]: {
    chain: BlockchainAbbreviation.ETH,
    name: 'Ethereum',
    explorerLink: 'https://etherscan.io/tx/{{txHash}}',
    testnetExplorerLink: 'https://sepolia.etherscan.io/tx/{{txHash}}',
    assets: [Currency.USD, Currency.EUR],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.ETH,
    isAceSupported: false,
    chainId: 1,
    testnetChainId: 11155111,
    usdcRole: {
      default: SynthRole.SYNTH_ETH_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_ETH_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
    eurcRole: {
      default: SynthRole.SYNTH_ETH_BLOCKCHAIN_EURC_ROLE,
      withdrawal: SynthRole.SYNTH_ETH_WITHDRAWAL_BLOCKCHAIN_EURC_ROLE,
    },
  },
  [BlockchainAbbreviation.EVMOS]: {
    chain: BlockchainAbbreviation.EVMOS,
    name: 'Evmos',
    explorerLink: 'https://evm.evmos.org/tx/{{txHash}}',
    testnetExplorerLink: 'https://evm.evmos.dev/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.EVMOS,
    isAceSupported: false,
    chainId: 9001,
    testnetChainId: 9000,
    usdcRole: {
      default: SynthRole.SYNTH_EVMOS_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_EVMOS_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.FLOW]: {
    chain: BlockchainAbbreviation.FLOW,
    name: 'Flow',
    explorerLink: 'https://flowscan.org/transaction/{{txHash}}',
    testnetExplorerLink: 'https://testnet.flowscan.org/transaction/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.FLOW,
    isAceSupported: false,
    chainId: 747,
    testnetChainId: 545,
    usdcRole: {
      default: SynthRole.SYNTH_FLOW_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_FLOW_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.HBAR]: {
    chain: BlockchainAbbreviation.HBAR,
    name: 'Hedera',
    explorerLink: 'https://app.dragonglass.me/hedera/transactions/{{txHash}}',
    testnetExplorerLink:
      'https://testnet.dragonglass.me/transactions/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: true,
    isBridged: false,
    icon: AssetIcon.HBAR,
    isAceSupported: false,
    chainId: 295,
    testnetChainId: 296,
    usdcRole: {
      default: SynthRole.SYNTH_HBAR_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_HBAR_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.LINEA]: {
    chain: BlockchainAbbreviation.LINEA,
    name: 'Linea',
    explorerLink: 'https://lineascan.build/tx/{{txHash}}',
    testnetExplorerLink: 'https://sepolia.lineascan.build/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.LINEA,
    isAceSupported: true,
    chainId: 59144,
    testnetChainId: 59141,
    deposits: {
      min: 8,
      max: 32,
      duration: 'hours',
    },
    usdcAddress: {
      test: '0xfece4462d57bd51a6a552365a011b95f0e16d9b7',
      live: '0x176211869ca2b568f2a7d4ee941e073a821ee1ff',
    },
    tokenMessengerAddress: {
      test: '0x8FE6B999Dc680CcFDD5Bf7EB0974218be2542DAA',
      live: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    },
    messageTransmitterAddress: {
      test: '0xE737e5cEBEEBa77EFE34D4aa090756590b1CE275',
      live: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    },
    usdcRole: {
      default: SynthRole.SYNTH_LINEA_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_LINEA_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  // TODO: fill in details for the MANA chain
  [BlockchainAbbreviation.MANA]: {
    chain: BlockchainAbbreviation.MANA,
    name: 'Mana',
    // TODO: fill in explorerLink
    explorerLink: '',
    // TODO: fill in testnetExplorerLink
    testnetExplorerLink: '',
    assets: [],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.MANA,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_MANA_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_MANA_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.NEAR]: {
    chain: BlockchainAbbreviation.NEAR,
    name: 'NEAR',
    explorerLink: 'https://nearblocks.io/txns/{{txHash}}',
    testnetExplorerLink: 'https://testnet.nearblocks.io/txns/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.NEAR,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_NEAR_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_NEAR_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.NOBLE]: {
    chain: BlockchainAbbreviation.NOBLE,
    name: 'Noble',
    explorerLink: 'https://www.mintscan.io/noble/tx/{{txHash}}',
    testnetExplorerLink: 'https://www.mintscan.io/noble-testnet/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.NOBLE,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_NOBLE_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_NOBLE_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.OP]: {
    chain: BlockchainAbbreviation.OP,
    name: 'OP',
    explorerLink: 'https://optimistic.etherscan.io/tx/{{txHash}}',
    testnetExplorerLink: 'https://sepolia-optimism.etherscan.io/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.OP,
    isAceSupported: false,
    chainId: 10,
    testnetChainId: 11155420,
    usdcRole: {
      default: SynthRole.SYNTH_OP_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_OP_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.PAH]: {
    chain: BlockchainAbbreviation.PAH,
    name: 'Polkadot Asset Hub',
    explorerLink: 'https://assethub-polkadot.subscan.io/extrinsic/{{txHash}}',
    testnetExplorerLink:
      'https://assethub-westend.subscan.io/extrinsic/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.PAH,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_PAH_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_PAH_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.POLY]: {
    chain: BlockchainAbbreviation.POLY,
    name: 'Polygon',
    explorerLink: 'https://polygonscan.com/tx/{{txHash}}',
    testnetExplorerLink: 'https://www.oklink.com/amoy/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.POLY,
    isAceSupported: false,
    chainId: 137,
    testnetChainId: 80002,
    usdcRole: {
      default: SynthRole.SYNTH_POLY_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_POLY_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.MATIC]: {
    chain: BlockchainAbbreviation.MATIC,
    name: 'Matic',
    explorerLink: 'https://polygonscan.com/tx/{{txHash}}',
    testnetExplorerLink: 'https://www.oklink.com/amoy/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: true,
    icon: AssetIcon.MATIC,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_MATIC_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_MATIC_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.SEI]: {
    chain: BlockchainAbbreviation.SEI,
    name: 'Sei',
    explorerLink: 'https://seitrace.com/tx/{{txHash}}?chain=pacific-1',
    testnetExplorerLink: 'https://seitrace.com/tx/{{txHash}}?chain=atlantic-2',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.SEI,
    isAceSupported: true,
    chainId: 1329,
    testnetChainId: 1328,
    usdcAddress: {
      test: '0x4fCF1784B31630811181f670Aea7A7bEF803eaED',
      live: '0xe15fC38F6D8c56aF07bbCBe3BAf5708A2Bf42392',
    },
    tokenMessengerAddress: {
      test: '0x8FE6B999Dc680CcFDD5Bf7EB0974218be2542DAA',
      live: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    },
    messageTransmitterAddress: {
      test: '0xE737e5cEBEEBa77EFE34D4aa090756590b1CE275',
      live: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    },
    usdcRole: {
      default: SynthRole.SYNTH_SEI_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_SEI_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.SOL]: {
    chain: BlockchainAbbreviation.SOL,
    name: 'Solana',
    explorerLink: 'https://explorer.solana.com/tx/{{txHash}}',
    testnetExplorerLink:
      'https://explorer.solana.com/tx/{{txHash}}?cluster=devnet',
    assets: [Currency.USD, Currency.EUR],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.SOL,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_SOL_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_SOL_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
    eurcRole: {
      default: SynthRole.SYNTH_SOL_BLOCKCHAIN_EURC_ROLE,
      withdrawal: SynthRole.SYNTH_SOL_WITHDRAWAL_BLOCKCHAIN_EURC_ROLE,
    },
  },
  [BlockchainAbbreviation.SONIC]: {
    chain: BlockchainAbbreviation.SONIC,
    name: 'Sonic',
    explorerLink: 'https://sonicscan.org/tx/{{txHash}}',
    testnetExplorerLink: 'https://testnet.sonicscan.org/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.S,
    isAceSupported: true,
    chainId: 146,
    testnetChainId: 57054,
    usdcAddress: {
      test: '0xA4879Fed32Ecbef99399e5cbC247E533421C4eC6',
      live: '0x29219dd400f2Bf60E5a23d13Be72B486D4038894',
    },
    tokenMessengerAddress: {
      test: '0x8FE6B999Dc680CcFDD5Bf7EB0974218be2542DAA',
      live: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    },
    messageTransmitterAddress: {
      test: '0xE737e5cEBEEBa77EFE34D4aa090756590b1CE275',
      live: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    },
    usdcRole: {
      default: SynthRole.SYNTH_SONIC_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_SONIC_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.SUI]: {
    chain: BlockchainAbbreviation.SUI,
    name: 'Sui',
    explorerLink: 'https://suiscan.xyz/mainnet/tx/{{txHash}}',
    testnetExplorerLink: 'https://suiscan.xyz/testnet/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.SUI,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_SUI_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_SUI_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.TRX]: {
    chain: BlockchainAbbreviation.TRX,
    name: 'Tron',
    explorerLink: 'https://tronscan.org/#/transaction/{{txHash}}',
    testnetExplorerLink: 'https://shasta.tronscan.org/#/transaction/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.TRX,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_TRX_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_TRX_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.UNI]: {
    chain: BlockchainAbbreviation.UNI,
    name: 'Unichain',
    explorerLink: 'https://unichain.blockscout.com/tx/{{txHash}}',
    testnetExplorerLink:
      'https://unichain-sepolia.blockscout.com/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.UNI,
    isAceSupported: false,
    chainId: 130,
    testnetChainId: 1301,
    usdcRole: {
      default: SynthRole.SYNTH_UNI_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_UNI_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.WORLDCHAIN]: {
    chain: BlockchainAbbreviation.WORLDCHAIN,
    name: 'Worldchain',
    explorerLink: 'https://worldscan.org/tx/{{txHash}}',
    testnetExplorerLink: 'https://sepolia.worldscan.org/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.WLD,
    isAceSupported: true,
    chainId: 480,
    testnetChainId: 4801,
    usdcAddress: {
      test: '0x66145f38cbac35ca6f1dfb4914df98f1614aea88',
      live: '0x79a02482a880bce3f13e09da970dc34db4cd24d1',
    },
    tokenMessengerAddress: {
      test: '0x8FE6B999Dc680CcFDD5Bf7EB0974218be2542DAA',
      live: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    },
    messageTransmitterAddress: {
      test: '0xE737e5cEBEEBa77EFE34D4aa090756590b1CE275',
      live: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    },
    usdcRole: {
      default: SynthRole.SYNTH_WORLDCHAIN_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_WORLDCHAIN_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.XLM]: {
    chain: BlockchainAbbreviation.XLM,
    name: 'Stellar',
    explorerLink: 'https://stellar.expert/explorer/public/tx/{{txHash}}',
    testnetExplorerLink:
      'https://stellar.expert/explorer/testnet/tx/{{txHash}}',
    assets: [Currency.USD, Currency.EUR],
    hasMemo: true,
    isBridged: false,
    icon: AssetIcon.XLM,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_XLM_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_XLM_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
    eurcRole: {
      default: SynthRole.SYNTH_XLM_BLOCKCHAIN_EURC_ROLE,
      withdrawal: SynthRole.SYNTH_XLM_WITHDRAWAL_BLOCKCHAIN_EURC_ROLE,
    },
  },
  [BlockchainAbbreviation.XRP]: {
    chain: BlockchainAbbreviation.XRP,
    name: 'XRPL',
    explorerLink: 'https://livenet.xrpl.org/transactions/{{txHash}}',
    testnetExplorerLink: 'https://testnet.xrpl.org/transactions/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: true,
    isBridged: false,
    icon: AssetIcon.XRP,
    isAceSupported: false,
    chainId: null,
    testnetChainId: null,
    usdcRole: {
      default: SynthRole.SYNTH_XRP_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_XRP_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
  [BlockchainAbbreviation.ZKS]: {
    chain: BlockchainAbbreviation.ZKS,
    name: 'ZKsync',
    explorerLink: 'https://explorer.zksync.io/tx/{{txHash}}',
    testnetExplorerLink: 'https://sepolia.explorer.zksync.io/tx/{{txHash}}',
    assets: [Currency.USD],
    hasMemo: false,
    isBridged: false,
    icon: AssetIcon.ZKS,
    isAceSupported: false,
    chainId: 324,
    testnetChainId: 300,
    usdcRole: {
      default: SynthRole.SYNTH_ZKS_BLOCKCHAIN_ROLE,
      withdrawal: SynthRole.SYNTH_ZKS_WITHDRAWAL_BLOCKCHAIN_ROLE,
    },
  },
}

export const ACE_BLOCKCHAINS = Object.values(BLOCKCHAINS).filter(
  (b) => b.isAceSupported,
)

/**
 * Static Data: Blockchain details for blockchain assets permission.
 *
 * By default, a blockchain asset will be disabled if no corresponding permission set is provided.
 */
export const BLOCKCHAIN_ASSET_PERMISSIONS: BlockchainAssetPermissions = {
  [BlockchainAbbreviation.ALGO]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: ALGO_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: ALGO_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.APTOS]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: APTOS_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: APTOS_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.ARB]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: ARB_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: ARB_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.AVAX]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: AVAX_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: AVAX_BLOCKCHAIN_WITHDRAWAL,
    },
    [Currency.EUR]: {
      [BlockchainActivityType.ALL]: AVAX_BLOCKCHAIN_EURC,
      [BlockchainActivityType.WITHDRAWAL]: AVAX_BLOCKCHAIN_WITHDRAWAL_EURC,
    },
  },
  [BlockchainAbbreviation.BASE]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: BASE_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: BASE_BLOCKCHAIN_WITHDRAWAL,
    },
    [Currency.EUR]: {
      [BlockchainActivityType.ALL]: BASE_BLOCKCHAIN_EURC,
      [BlockchainActivityType.WITHDRAWAL]: BASE_BLOCKCHAIN_WITHDRAWAL_EURC,
    },
  },
  [BlockchainAbbreviation.BTC]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: BTC_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: BTC_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.CELO]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: CELO_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: CELO_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.CODEX]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: CODEX_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: CODEX_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.ETH]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: ETH_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: ETH_BLOCKCHAIN_WITHDRAWAL,
    },
    [Currency.EUR]: {
      [BlockchainActivityType.ALL]: ETH_BLOCKCHAIN_EURC,
      [BlockchainActivityType.WITHDRAWAL]: ETH_BLOCKCHAIN_WITHDRAWAL_EURC,
    },
  },
  [BlockchainAbbreviation.EVMOS]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: EVMOS_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: EVMOS_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.FLOW]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: FLOW_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: FLOW_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.HBAR]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: HBAR_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: HBAR_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.LINEA]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: LINEA_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: LINEA_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.MANA]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: MANA_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: MANA_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.MATIC]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: MATIC_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: MATIC_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.NEAR]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: NEAR_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: NEAR_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.NOBLE]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: NOBLE_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: NOBLE_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.OP]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: OP_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: OP_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.PAH]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: PAH_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: PAH_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.POLY]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: POLY_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: POLY_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.SOL]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: SOL_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: SOL_BLOCKCHAIN_WITHDRAWAL,
    },
    [Currency.EUR]: {
      [BlockchainActivityType.ALL]: SOL_BLOCKCHAIN_EURC,
      [BlockchainActivityType.WITHDRAWAL]: SOL_BLOCKCHAIN_WITHDRAWAL_EURC,
    },
  },
  [BlockchainAbbreviation.SONIC]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: SONIC_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: SONIC_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.SUI]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: SUI_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: SUI_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.TRX]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: TRX_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: TRX_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.UNI]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: UNI_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: UNI_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.WORLDCHAIN]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: WORLDCHAIN_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: WORLDCHAIN_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.XLM]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: XLM_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: XLM_BLOCKCHAIN_WITHDRAWAL,
    },
    [Currency.EUR]: {
      [BlockchainActivityType.ALL]: XLM_BLOCKCHAIN_EURC,
      [BlockchainActivityType.WITHDRAWAL]: XLM_BLOCKCHAIN_WITHDRAWAL_EURC,
    },
  },
  [BlockchainAbbreviation.XRP]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: XRP_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: XRP_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.ZKS]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: ZKS_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: ZKS_BLOCKCHAIN_WITHDRAWAL,
    },
  },
  [BlockchainAbbreviation.SEI]: {
    [Currency.USD]: {
      [BlockchainActivityType.ALL]: SEI_BLOCKCHAIN,
      [BlockchainActivityType.WITHDRAWAL]: SEI_BLOCKCHAIN_WITHDRAWAL,
    },
  },
}

/**
 * Static Data: TRM supported chains.
 */
export const TRM_SUPPORTED_CHAINS = [
  BlockchainAbbreviation.ALGO,
  BlockchainAbbreviation.ARB,
  BlockchainAbbreviation.AVAX,
  BlockchainAbbreviation.ETH,
  BlockchainAbbreviation.HBAR,
  BlockchainAbbreviation.OP,
  BlockchainAbbreviation.MATIC,
  BlockchainAbbreviation.POLY,
  BlockchainAbbreviation.SOL,
  BlockchainAbbreviation.XLM,
  BlockchainAbbreviation.XRP,
  BlockchainAbbreviation.TRX,
]

export const getBlockchain = (
  chain: Maybe<BlockchainAbbreviation> | undefined,
) => (chain != null ? BLOCKCHAINS[chain] : undefined)
